import { useState, useCallback, useRef, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { useClub } from '../hooks/useClub';

// ─── File type definitions ────────────────────────────────────────────────────
const FILE_TYPES = {
  players_report_:  { label: 'ACTIVE PLAYERS',    icon: '🏃', phase: 1, required: true,  description: 'Active member profiles — truth source',        color: '#22c55e' },
  list_:            { label: 'MEMBERSHIPS REPORT', icon: '🌱', phase: 1, required: true,  description: 'Subscription status — truth source + T2M',     color: '#eab308' },
  checkins_:        { label: 'CHECK-IN EVENTS',    icon: '✅', phase: 2, required: false, description: 'Individual check-in records — scoring',         color: '#3b82f6' },
  player_visits_:   { label: 'VISIT AGGREGATES',   icon: '📊', phase: 2, required: false, description: 'Visit summary — scoring (manual club assign)',  color: '#3b82f6', manualClub: true },
  players2_report_: { label: 'ALL USERS REPORT',   icon: '👥', phase: 2, required: false, description: 'Full player database — weekly enrichment',      color: '#a855f7' },
  activity_report_: { label: 'SALES ACTIVITY',     icon: '🏓', phase: 2, required: false, description: 'PB101 registrations, program bookings',         color: '#f97316' },
  payments_report_: { label: 'ALL SALES REPORT',   icon: '💳', phase: 2, required: false, description: 'Payment-level detail, incl. reversals',          color: '#06b6d4' },
  revenue_recognition_: { label: 'REVENUE RECOGNITION', icon: '📈', phase: 2, required: false, description: 'Reliable $ amounts — feeds Revenue Analytics', color: '#22c55e' },
};

const CLUBS = [
  { id: 'WA',  name: 'West Allis' },
  { id: 'MF',  name: 'Menomonee Falls' },
  { id: 'GB',  name: 'Green Bay' },
  { id: 'DEN', name: 'Denton' },
  { id: 'LEW', name: 'Lewisville' },
  { id: 'MCK', name: 'McKinney' },
];

function detectClubFromText(text) {
  if (!text) return null;
  const lower = text.toLowerCase();
  if (lower.includes('west allis')) return 'WA';
  if (lower.includes('menomonee falls') || lower.includes('menomonee')) return 'MF';
  if (lower.includes('green bay')) return 'GB';
  if (lower.includes('denton')) return 'DEN';
  if (lower.includes('lewisville')) return 'LEW';
  if (lower.includes('mckinney') || lower.includes('mc kinney')) return 'MCK';
  return null;
}

// Try filename first, then reads xlsx header cell for club detection
function detectClubFromFile(file) {
  return detectClubFromText(file.name);
}

function detectClubFromHeader(filename) {
  return detectClubFromText(filename);
}

function detectFileType(filename) {
  const lower = filename.toLowerCase();
  return Object.keys(FILE_TYPES).find(prefix => lower.startsWith(prefix)) || null;
}

// ─── Per-club status card ─────────────────────────────────────────────────────
function ClubStatusCard({ club, files, manualClub, onManualClubChange, runResult, phase }) {
  const fileTypes = Object.keys(files);
  const hasPlayers = fileTypes.includes('players_report_');
  const hasList    = fileTypes.includes('list_');
  const hasCheckins = fileTypes.includes('checkins_');
  const hasVisits  = fileTypes.includes('player_visits_');
  const hasPlayers2 = fileTypes.includes('players2_report_');
  const hasActivity = fileTypes.includes('activity_report_');
  const hasPayments = fileTypes.includes('payments_report_');
  const hasRevenue = fileTypes.includes('revenue_recognition_');
  const isReady    = hasPlayers && hasList;
  const visitsFile = files['player_visits_'];

  const statusColor = runResult
    ? runResult.status === 'complete' ? '#22c55e'
    : runResult.status === 'running'  ? '#eab308'
    : runResult.status === 'error'    ? '#ef4444'
    : '#3b82f6'
    : isReady ? '#22c55e' : '#ef4444';

  return (
    <div style={{
      background: 'var(--surface-2)',
      border: `1px solid ${statusColor}40`,
      borderRadius: 8, padding: '10px 14px',
      marginBottom: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 10, fontWeight: 800, color: statusColor,
            background: `${statusColor}15`, border: `1px solid ${statusColor}30`,
            borderRadius: 4, padding: '2px 8px', letterSpacing: '0.06em',
          }}>
            {club.id}
          </span>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{club.name}</span>
        </div>

        {/* Run status */}
        {runResult && (
          <div style={{ fontSize: 11, color: statusColor, fontWeight: 600 }}>
            {runResult.status === 'complete' && '✅ Complete'}
            {runResult.status === 'running'  && '⏳ Processing…'}
            {runResult.status === 'queued'   && '⏸ Queued'}
            {runResult.status === 'error'    && '❌ Error'}
          </div>
        )}
      </div>

      {/* File status pills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: hasPlayers && !hasList || !hasPlayers ? 8 : 0 }}>
        {[
          { key: 'players_report_', label: '🏃 Players',   has: hasPlayers,  required: true },
          { key: 'list_',           label: '🌱 List',       has: hasList,     required: true },
          { key: 'checkins_',       label: '✅ Checkins',   has: hasCheckins, required: false },
          { key: 'player_visits_',  label: '📊 Visits',     has: hasVisits,   required: false },
          { key: 'players2_report_',label: '👥 Users',      has: hasPlayers2, required: false },
          { key: 'activity_report_',label: '🏓 Activity',   has: hasActivity, required: false },
          { key: 'payments_report_',label: '💳 Payments', has: hasPayments, required: false },
          { key: 'revenue_recognition_',label: '📈 Revenue', has: hasRevenue, required: false },
        ].map(f => (
          <span key={f.key} style={{
            fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
            background: f.has ? (f.required ? '#22c55e15' : '#ffffff08') : (f.required ? '#ef444415' : '#33333320'),
            border: `1px solid ${f.has ? (f.required ? '#22c55e40' : '#44444460') : (f.required ? '#ef444440' : '#33333340')}`,
            color: f.has ? (f.required ? '#22c55e' : '#888') : (f.required ? '#ef4444' : '#444'),
          }}>
            {f.has ? '✓' : f.required ? '✗' : '—'} {f.label}
          </span>
        ))}
      </div>

      {/* Visits assigned — show confirmation only, no duplicate dropdown */}
      {visitsFile && manualClub && manualClub[visitsFile.name] && (
        <div style={{ marginTop: 6, fontSize: 10, color: '#888' }}>
          📊 {visitsFile.name} — assigned to {CLUBS.find(c => c.id === manualClub[visitsFile.name])?.name || manualClub[visitsFile.name]} ✓
        </div>
      )}

      {/* Missing required files warning */}
      {(!hasPlayers || !hasList) && (
        <div style={{
          marginTop: 8, fontSize: 11, color: '#ef4444',
          padding: '4px 8px', borderRadius: 4,
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
        }}>
          Missing required: {!hasPlayers ? 'players_report_ ' : ''}{!hasList ? 'list_' : ''} — pipeline will not run for this club
        </div>
      )}

      {/* Progress bar during run */}
      {runResult?.status === 'running' && (
        <div style={{ marginTop: 8, background: '#1a1a1a', borderRadius: 4, height: 4, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 4,
            background: '#eab308',
            width: `${runResult.progress || 30}%`,
            transition: 'width 0.5s',
          }} />
        </div>
      )}

      {/* Completion stats */}
      {runResult?.status === 'complete' && runResult.stats && (
        <div style={{ marginTop: 8, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { label: 'Active prospects', value: runResult.stats.leadsScored },
            { label: 'Members confirmed', value: runResult.stats.membersConfirmed },
            { label: 'Suppressed', value: runResult.stats.membersSuppressed },
            { label: 'Trials', value: runResult.stats.trialsCreated },
          ].filter(s => s.value != null).map(s => (
            <div key={s.label} style={{ fontSize: 10 }}>
              <span style={{ fontWeight: 700, color: 'var(--text)' }}>{s.value?.toLocaleString()}</span>
              <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>{s.label}</span>
            </div>
          ))}
          {/* Sprint 21 continuation — always shown, including "0 Unmatched"
              on a clean run. Not filtered by != null like the stats above,
              since 0 is itself the confirmation worth seeing. Styled orange
              (matching the Mismatch Queue Run Results badge) whenever it's
              actually nonzero, so a real issue doesn't blend in with the
              routine numbers next to it. */}
          {runResult.stats.unmatchedNoName != null && (
            <div style={{ fontSize: 10 }}>
              <span style={{
                fontWeight: 700,
                color: runResult.stats.unmatchedNoName > 0 ? '#f97316' : 'var(--text)',
              }}>
                {runResult.stats.unmatchedNoName.toLocaleString()}
              </span>
              <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>
                Unmatched{runResult.stats.unmatchedNoName > 0 ? ' — needs review' : ''}
              </span>
            </div>
          )}
        </div>
      )}

      {runResult?.status === 'error' && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#ef4444' }}>
          {runResult.errorMessage || 'Pipeline error — check logs'}
        </div>
      )}
    </div>
  );
}

// ─── Unassigned visits panel ──────────────────────────────────────────────────
function UnassignedVisitsPanel({ files, manualAssignments, onAssign }) {
  if (files.length === 0) return null;
  return (
    <div style={{
      background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)',
      borderRadius: 8, padding: '12px 14px', marginBottom: 16,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
        📊 Visit Files — Manual Club Assignment Required
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
        Visit files have no club identifier in the filename. Assign each to its club before running.
      </div>
      {files.map(f => (
        <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: 'var(--text)', flex: 1, fontFamily: 'monospace' }}>{f.name}</span>
          <select
            className="field-select"
            value={manualAssignments[f.name] || ''}
            onChange={e => onAssign(f.name, e.target.value)}
            style={{ fontSize: 11, padding: '3px 8px', height: 'auto', minWidth: 140 }}
          >
            <option value="">Assign club…</option>
            {CLUBS.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {manualAssignments[f.name] && (
            <span style={{ fontSize: 10, color: '#22c55e' }}>✓</span>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function UploadPortal() {
  const { post, get } = useApi();

  // All dropped files, grouped by detected club
  const [droppedFiles, setDroppedFiles]         = useState([]); // array of File objects
  const [manualAssignments, setManualAssignments] = useState({}); // filename → clubId
  const [phase, setPhase]                       = useState('drop'); // drop | validated | running | complete | error
  const [validation, setValidation]             = useState(null);
  const [clubRunResults, setClubRunResults]     = useState({}); // clubId → { status, stats, progress }
  const [globalError, setGlobalError]           = useState(null);
  const [completeSummary, setCompleteSummary]   = useState(null);
  const [isDragging, setIsDragging]             = useState(false);
  const [detectedClubCache, setDetectedClubCache] = useState({});
  const [uploadMode, setUploadMode]             = useState('daily'); // daily | initial
  // Sprint 21 continuation — independent confirmation of each club's real
  // last-run outcome straight from processing_log, so a "Failed to fetch"
  // browser error (repeatedly a false alarm on large files) never has to
  // mean opening a SQL session to find out what actually happened.
  const [recentStatus, setRecentStatus]         = useState(null);
  const [recentStatusLoading, setRecentStatusLoading] = useState(false);
  const dropRef = useRef(null);
  const inputRef = useRef(null);

  function loadRecentStatus() {
    setRecentStatusLoading(true);
    get('/upload/recent-status?days=3')
      .then(d => setRecentStatus(d?.runs || []))
      .catch(console.error)
      .finally(() => setRecentStatusLoading(false));
  }

  useEffect(() => { loadRecentStatus(); }, []);

  // ── Group files by club ───────────────────────────────────────────────────
  function groupFilesByClub() {
    const groups = {}; // clubId → { fileType → File }
    const unassignedVisits = [];

    droppedFiles.forEach(file => {
      const fileType = detectFileType(file.name);
      if (!fileType) return;

      if (fileType === 'player_visits_') {
        const assignedClub = manualAssignments[file.name];
        if (assignedClub) {
          if (!groups[assignedClub]) groups[assignedClub] = {};
          groups[assignedClub][fileType] = file;
        } else {
          unassignedVisits.push(file);
        }
        return;
      }

      // Use async-detected club from cache, fall back to filename detection
      const detectedClub = detectedClubCache[file.name] || detectClubFromHeader(file.name);
      if (detectedClub) {
        if (!groups[detectedClub]) groups[detectedClub] = {};
        groups[detectedClub][fileType] = file;
      }
    });

    return { groups, unassignedVisits };
  }

  const { groups: clubGroups, unassignedVisits } = groupFilesByClub();
  const detectedClubs = Object.keys(clubGroups || {});
  const unrecognizedFiles = droppedFiles.filter(f => !detectFileType(f.name));
  const visitFiles = droppedFiles.filter(f => detectFileType(f.name) === 'player_visits_');

  // Prevent browser from opening dropped files (causes black screen)
  useEffect(() => {
    const prevent = e => { e.preventDefault(); e.stopPropagation(); };
    document.addEventListener('dragover', prevent);
    document.addEventListener('drop', prevent);
    return () => {
      document.removeEventListener('dragover', prevent);
      document.removeEventListener('drop', prevent);
    };
  }, []);

  // ── File drop handler ─────────────────────────────────────────────────────
  const addFiles = useCallback((newFiles) => {
    const xlsx = newFiles.filter(f => f.name.endsWith('.xlsx'));
    if (xlsx.length === 0) return;
    setDroppedFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      return [...prev, ...xlsx.filter(f => !existingNames.has(f.name))];
    });
    // Sync club detection from filename
    const cache = {};
    xlsx.forEach(f => {
      const club = detectClubFromFile(f);
      if (club) cache[f.name] = club;
    });
    setDetectedClubCache(prev => ({ ...prev, ...cache }));
    setPhase('drop');
    setValidation(null);
    setGlobalError(null);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const handleFileInput = (e) => {
    addFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const removeFile = (filename) => {
    setDroppedFiles(prev => prev.filter(f => f.name !== filename));
    setManualAssignments(prev => { const n = {...prev}; delete n[filename]; return n; });
  };

  const handleManualAssign = (filename, clubId) => {
    setManualAssignments(prev => ({ ...prev, [filename]: clubId }));
  };

  // ── Validate ──────────────────────────────────────────────────────────────
  const handleValidate = async () => {
    if (droppedFiles.length === 0) return;
    setPhase('validating');
    setGlobalError(null);

    try {
      const formData = new FormData();
      formData.append('mode', uploadMode);
      formData.append('isBatch', 'true');
      droppedFiles.forEach(f => formData.append('files', f));
      Object.entries(manualAssignments).forEach(([filename, clubId]) => {
        formData.append(`manualClub_${filename}`, clubId);
      });

      const result = await post('/upload/validate', formData);
      setValidation(result);
      // Update club cache from server-detected clubs
      if (result.files) {
        const cache = {};
        result.files.forEach(f => {
          if (f.detectedClub) cache[f.filename] = f.detectedClub;
        });
        if (Object.keys(cache).length > 0) {
          setDetectedClubCache(prev => ({ ...prev, ...cache }));
        }
      }
      // Small delay to let cache update render before phase change
      setTimeout(() => setPhase('validated'), 50);
    } catch (err) {
      setGlobalError(err.message);
      setPhase('drop');
    }
  };

  // ── Run pipeline for all clubs sequentially ───────────────────────────────
  const handleRun = async () => {
    setPhase('running');
    setGlobalError(null);

    // Initialize all club statuses
    const initialResults = {};
    detectedClubs.forEach(clubId => {
      const files = clubGroups[clubId];
      const hasRequired = files['players_report_'] && files['list_'];
      initialResults[clubId] = {
        status: hasRequired ? 'queued' : 'skipped',
        reason: hasRequired ? null : 'Missing required files',
      };
    });
    setClubRunResults(initialResults);

    const results = { ...initialResults };
    let allComplete = true;

    // Process clubs sequentially — Phase 1 (truth table) first
    // Sort so players_report_ + list_ clubs run first
    const clubsToRun = detectedClubs.filter(id => results[id].status === 'queued');

    for (const clubId of clubsToRun) {
      // Mark as running
      results[clubId] = { status: 'running', progress: 10 };
      setClubRunResults({ ...results });

      try {
        const formData = new FormData();
        formData.append('clubId', clubId);
        formData.append('mode', uploadMode);
        formData.append('isBatch', 'true');

        const clubFiles = clubGroups[clubId];
        Object.values(clubFiles).forEach(f => formData.append('files', f));

        // Kick off pipeline
        const { runId } = await post('/upload/run', formData);

        // Poll for completion
        let done = false;
        let attempts = 0;
        while (!done && attempts < 120) {
          await new Promise(r => setTimeout(r, 2000));
          attempts++;
          try {
            const status = await get(`/upload/status/${runId}`);
            results[clubId] = {
              status: status.status === 'complete' ? 'running' : status.status,
              progress: getProgress(status.steps),
              runId,
            };
            setClubRunResults({ ...results });

            if (status.status === 'complete' || status.status === 'error') {
              done = true;
              if (status.status === 'complete') {
                const summary = await get(`/upload/summary/${runId}`);
                results[clubId] = {
                  status: 'complete',
                  stats: summary.stats,
                  warnings: summary.warnings,
                  aiSummary: summary.aiSummary,
                };
              } else {
                results[clubId] = {
                  status: 'error',
                  errorMessage: status.errorMessage,
                };
                allComplete = false;
              }
              setClubRunResults({ ...results });
            }
          } catch (pollErr) {
            console.error('Poll error:', pollErr);
          }
        }

        if (!done) {
          results[clubId] = { status: 'error', errorMessage: 'Timed out after 4 minutes' };
          setClubRunResults({ ...results });
          allComplete = false;
        }

      } catch (err) {
        results[clubId] = { status: 'error', errorMessage: err.message };
        setClubRunResults({ ...results });
        allComplete = false;
      }
    }

    // Build completion summary
    const completed = Object.values(results).filter(r => r.status === 'complete');
    const errors    = Object.values(results).filter(r => r.status === 'error');
    const skipped   = Object.values(results).filter(r => r.status === 'skipped');

    setCompleteSummary({
      completedAt: new Date().toISOString(),
      clubsComplete: completed.length,
      clubsError: errors.length,
      clubsSkipped: skipped.length,
      results,
    });

    setPhase(allComplete ? 'complete' : 'error');

    // Sprint 21 continuation — refresh straight from processing_log now
    // that the batch has finished, so any club that showed "Failed to
    // fetch" gets reconciled against the real server-side outcome
    // automatically, without waiting for a manual page reload.
    loadRecentStatus();
  };

  function getProgress(steps) {
    if (!steps) return 20;
    const vals = Object.values(steps);
    const done = vals.filter(v => v === 'done').length;
    return Math.round((done / vals.length) * 100);
  }

  const handleReset = () => {
    setDroppedFiles([]);
    setManualAssignments({});
    setPhase('drop');
    setValidation(null);
    setGlobalError(null);
    setClubRunResults({});
    setCompleteSummary(null);
  };

  const canValidate = droppedFiles.length > 0 && phase === 'drop';
  const canRun = phase === 'validated' && detectedClubs.some(id => {
    const files = clubGroups[id];
    return files['players_report_'] && files['list_'];
  });

  const readyClubs  = detectedClubs.filter(id => clubGroups[id]['players_report_'] && clubGroups[id]['list_']);
  const blockedClubs = detectedClubs.filter(id => !clubGroups[id]['players_report_'] || !clubGroups[id]['list_']);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="upload-portal">
      <div className="page-header">
        <h1>PBP Data Upload</h1>
        <p className="page-subtitle">Import PlayByPoint exports to update member data and pipeline records</p>
      </div>

      {/* ── Recent Upload Status — Sprint 21 continuation ──────────────────
          Reads processing_log directly via a dedicated endpoint, independent
          of whatever the drop/upload flow above is doing. A club showing
          "Failed to fetch" in the run below is very often just the browser's
          own fetch timing out on a large file (players2_report_ especially)
          while the server-side run finishes fine — this panel confirms the
          real outcome without needing a SQL session. */}
      <div style={{
        background: 'var(--surface-2)', border: '1px solid var(--border)',
        borderRadius: 8, padding: '10px 14px', marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: recentStatus?.length ? 8 : 0 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Recent Upload Status (last 3 days, straight from the database)
          </span>
          <button
            onClick={loadRecentStatus}
            disabled={recentStatusLoading}
            className="btn btn-ghost btn-sm"
            style={{ fontSize: 11 }}
          >
            {recentStatusLoading ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>
        {!recentStatusLoading && (!recentStatus || recentStatus.length === 0) && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No runs in the last 3 days.</div>
        )}
        {recentStatus && recentStatus.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
            {Object.entries(
              recentStatus.reduce((acc, r) => {
                (acc[r.location_id] = acc[r.location_id] || []).push(r);
                return acc;
              }, {})
            ).map(([clubId, runs]) => {
              const latest = runs[0];
              const ok = latest.status === 'complete';
              return (
                <div key={clubId} style={{ fontSize: 12 }}>
                  <span style={{ fontWeight: 700, color: 'var(--text)' }}>{clubId}</span>{' '}
                  <span style={{ color: ok ? '#22c55e' : '#ef4444', fontWeight: 700 }}>
                    {ok ? '✓ complete' : `✗ ${latest.status}`}
                  </span>{' '}
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {new Date(latest.started_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Mode tabs ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { key: 'daily',   label: 'DAILY UPLOAD',  desc: 'Standard run — truth table + pipeline', icon: '📅', color: '#3b82f6' },
          { key: 'initial', label: 'INITIAL LOAD',  desc: 'First-time club setup — builds DB from scratch', icon: '🏗️', color: '#eab308' },
        ].map(m => (
          <button
            key={m.key}
            disabled={phase === 'running'}
            onClick={() => { setUploadMode(m.key); setPhase('drop'); setValidation(null); }}
            style={{
              flex: 1,
              background: uploadMode === m.key ? `${m.color}20` : 'var(--surface-2)',
              border: `2px solid ${uploadMode === m.key ? m.color : 'var(--border)'}`,
              borderRadius: 8, padding: '10px 16px', cursor: 'pointer', textAlign: 'left',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: uploadMode === m.key ? m.color : 'var(--text)', letterSpacing: 1, marginBottom: 3 }}>
              {m.icon} {m.label}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{m.desc}</div>
          </button>
        ))}
      </div>

      {uploadMode === 'initial' && (
        <div style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--yellow)' }}>
          ⚠️ <strong>Initial Load mode</strong> — deduplication disabled. Use only for a club's first-ever import.
        </div>
      )}

      {/* ── Drop zone ── */}
      {(phase === 'drop' || phase === 'validated') && (
        <div
          ref={dropRef}
          onDragOver={e => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
          onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
          onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
          onDrop={e => { setIsDragging(false); handleDrop(e); }}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${isDragging ? '#3b82f6' : 'var(--border)'}`,
            borderRadius: 10, padding: '28px 20px',
            textAlign: 'center', cursor: 'pointer',
            background: isDragging ? 'rgba(59,130,246,0.08)' : 'var(--surface-2)',
            marginBottom: 16, transition: 'all 0.15s',
            outline: isDragging ? '2px solid rgba(59,130,246,0.3)' : 'none',
          }}
        >
          <input ref={inputRef} type="file" accept=".xlsx" multiple style={{ display: 'none' }} onChange={handleFileInput} />
          <div style={{ fontSize: 28, marginBottom: 8 }}>📂</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
            Drop all PbP export files here
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Drop any combination of files from any clubs — club is auto-detected from filename.<br/>
            Visit files (player_visits_) require manual club assignment below.
          </div>
          {droppedFiles.length > 0 && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#22c55e', fontWeight: 600 }}>
              {droppedFiles.length} file{droppedFiles.length !== 1 ? 's' : ''} loaded — drop more or proceed to validate
            </div>
          )}
        </div>
      )}

      {/* ── File list ── */}
      {droppedFiles.length > 0 && phase !== 'running' && phase !== 'complete' && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Loaded Files ({droppedFiles.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {droppedFiles.map(f => {
              const ft = detectFileType(f.name);
              const fc = ft ? FILE_TYPES[ft] : null;
              const club = detectClubFromHeader(f.name) || manualAssignments[f.name];
              return (
                <div key={f.name} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: fc ? `${fc.color}10` : 'var(--surface-3)',
                  border: `1px solid ${fc ? `${fc.color}30` : 'var(--border)'}`,
                  borderRadius: 5, padding: '3px 8px', fontSize: 11,
                }}>
                  <span>{fc?.icon || '📄'}</span>
                  <span style={{ color: 'var(--text)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.name}
                  </span>
                  {club && <span style={{ color: fc?.color || '#888', fontWeight: 700 }}>{club}</span>}
                  {!ft && <span style={{ color: '#ef4444', fontSize: 9 }}>UNKNOWN</span>}
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFile(f.name); }}
                    style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 12, padding: 0, lineHeight: 1 }}
                  >×</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Manual visit file assignment ── */}
      {visitFiles.length > 0 && phase !== 'running' && phase !== 'complete' && (
        <UnassignedVisitsPanel
          files={visitFiles}
          manualAssignments={manualAssignments}
          onAssign={handleManualAssign}
        />
      )}

      {/* ── Club detection summary ── */}
      {detectedClubs.length > 0 && phase !== 'running' && phase !== 'complete' && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Detected Clubs — {readyClubs.length} ready · {blockedClubs.length} missing required files
          </div>
          {detectedClubs.map(clubId => {
            const club = CLUBS.find(c => c.id === clubId);
            return (
              <ClubStatusCard
                key={clubId}
                club={club || { id: clubId, name: clubId }}
                files={clubGroups[clubId]}
                manualClub={manualAssignments}
                onManualClubChange={handleManualAssign}
                runResult={null}
                phase={phase}
              />
            );
          })}
        </div>
      )}

      {/* ── Unrecognized files warning ── */}
      {unrecognizedFiles.length > 0 && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: '#ef4444' }}>
          ⚠ {unrecognizedFiles.length} unrecognized file{unrecognizedFiles.length !== 1 ? 's' : ''} will be ignored: {unrecognizedFiles.map(f => f.name).join(', ')}
        </div>
      )}

      {/* ── Validation results ── */}
      {validation && phase === 'validated' && (
        <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Validation Results
          </div>
          {validation.files?.map(f => (
            <div key={f.filename} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: 'var(--text)', fontFamily: 'monospace' }}>{f.filename}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{f.rowCount?.toLocaleString()} rows</span>
              </div>
              {f.warnings?.map((w, i) => (
                <div key={i} style={{ fontSize: 11, color: w.level === 'critical' ? '#ef4444' : '#eab308', marginTop: 2 }}>
                  {w.level === 'critical' ? '🔴' : '🟡'} {w.message}
                </div>
              ))}
            </div>
          ))}
          {validation.summary && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
              {validation.summary.totalRows?.toLocaleString()} total rows · {validation.summary.fileCount} files
            </div>
          )}
        </div>
      )}

      {globalError && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#ef4444' }}>
          {globalError}
        </div>
      )}

      {/* ── Actions ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {phase === 'drop' && (
          <button className="btn-primary" onClick={handleValidate} disabled={!canValidate}>
            Validate Files
          </button>
        )}
        {phase === 'validating' && (
          <button className="btn-primary" disabled>Validating…</button>
        )}
        {phase === 'validated' && (
          <>
            <button className="btn-primary" onClick={handleRun} disabled={!canRun}>
              🚀 Run Pipeline — {readyClubs.length} club{readyClubs.length !== 1 ? 's' : ''}
            </button>
            <button className="btn-secondary" onClick={handleReset}>Start Over</button>
          </>
        )}
        {phase === 'running' && (
          <button className="btn-primary" disabled>Running…</button>
        )}
        {(phase === 'complete' || phase === 'error') && (
          <button className="btn-secondary" onClick={handleReset}>Upload Another Batch</button>
        )}
      </div>

      {/* ── Running — per-club progress ── */}
      {phase === 'running' && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Pipeline Progress — Processing sequentially
          </div>
          {detectedClubs.map(clubId => {
            const club = CLUBS.find(c => c.id === clubId);
            return (
              <ClubStatusCard
                key={clubId}
                club={club || { id: clubId, name: clubId }}
                files={clubGroups[clubId]}
                manualClub={manualAssignments}
                onManualClubChange={handleManualAssign}
                runResult={clubRunResults[clubId]}
                phase={phase}
              />
            );
          })}
        </div>
      )}

      {/* ── Complete summary ── */}
      {(phase === 'complete' || phase === 'error') && completeSummary && (
        <div style={{ background: 'var(--surface-2)', border: `1px solid ${phase === 'complete' ? '#22c55e40' : '#ef444440'}`, borderRadius: 10, padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: phase === 'complete' ? '#22c55e' : '#ef4444' }}>
              {phase === 'complete' ? '✅ All Clubs Complete' : '⚠ Completed with Errors'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              {new Date(completeSummary.completedAt).toLocaleString()}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12 }}>
              <span style={{ fontWeight: 700, color: '#22c55e' }}>{completeSummary.clubsComplete}</span>
              <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>clubs complete</span>
            </div>
            {completeSummary.clubsError > 0 && (
              <div style={{ fontSize: 12 }}>
                <span style={{ fontWeight: 700, color: '#ef4444' }}>{completeSummary.clubsError}</span>
                <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>errors</span>
              </div>
            )}
            {completeSummary.clubsSkipped > 0 && (
              <div style={{ fontSize: 12 }}>
                <span style={{ fontWeight: 700, color: '#888' }}>{completeSummary.clubsSkipped}</span>
                <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>skipped (missing required files)</span>
              </div>
            )}
            {/* Sprint 21 continuation — total unmatched-record count across all
                clubs in this batch, summed client-side from each club's
                already-fetched stats rather than a new API call. Shown even
                at 0 for the same reason as the per-club figure below: a
                confirmed zero is itself useful confidence, not just an
                absence of a problem. */}
            {(() => {
              const totalUnmatched = Object.values(completeSummary.results || {})
                .reduce((sum, r) => sum + (r.stats?.unmatchedNoName || 0), 0);
              return (
                <div style={{ fontSize: 12 }}>
                  <span style={{ fontWeight: 700, color: totalUnmatched > 0 ? '#f97316' : 'var(--text)' }}>
                    {totalUnmatched.toLocaleString()}
                  </span>
                  <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>
                    unmatched{totalUnmatched > 0 ? ' — review in Mismatch Queue' : ''}
                  </span>
                </div>
              );
            })()}
          </div>

          {/* Per-club results */}
          <div style={{ marginBottom: 14 }}>
            {detectedClubs.map(clubId => {
              const club = CLUBS.find(c => c.id === clubId);
              return (
                <ClubStatusCard
                  key={clubId}
                  club={club || { id: clubId, name: clubId }}
                  files={clubGroups[clubId]}
                  manualClub={manualAssignments}
                  onManualClubChange={() => {}}
                  runResult={clubRunResults[clubId]}
                  phase={phase}
                />
              );
            })}
          </div>

          {/* AI summaries */}
          {Object.entries(clubRunResults)
            .filter(([, r]) => r.aiSummary)
            .map(([clubId, r]) => (
              <div key={clubId} style={{ marginBottom: 10, padding: '10px 12px', background: 'var(--surface-3)', borderRadius: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                  {CLUBS.find(c => c.id === clubId)?.name || clubId} — Run Summary
                </div>
                <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.5 }}>{r.aiSummary}</div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
